import { SERVICES } from '@client/components/pricing/SeasonalPricing/utils/estimateDisplay';
import type { EstimateServiceBreakdownItem } from '@client/components/pricing/SeasonalPricing/utils/estimateDisplay';
import type { PricingEstimationService } from '@client/components/pricing/types';
import { FREE_SERVICE_LABEL, NOT_OFFERED_LABEL } from '@constants/pricing';
import { Checkbox } from '@tkww/orion-web-ds-input';
import { TextBody } from '@tkww/orion-web-ds-text';
import React from 'react';
import { LocalAverageTooltip } from '../../LocalAverageTooltip';
import { ServicesMinimumSpendInfo } from '../../ServicesMinimumSpendInfo';
import Styles from './styles.scss';

interface ServicesListProps {
	selectedServices: PricingEstimationService[];
	onToggle: (key: PricingEstimationService) => void;
	servicePrices: Partial<
		Record<PricingEstimationService, EstimateServiceBreakdownItem | null>
	>;
	showMinimumSpendInfo: boolean;
}

export const ServicesList = ({
	selectedServices: selectedKeys,
	onToggle,
	servicePrices,
	showMinimumSpendInfo,
}: ServicesListProps) => {
	const items = SERVICES.map(({ key, value, isPerGuest }) => {
		const servicePrice = servicePrices[key];

		if (servicePrice && 'unavailable' in servicePrice) {
			return {
				key,
				title: value,
				price: undefined,
				isLocalAverage: false,
				isStartingPrice: false,
				isFree: false,
				isUnavailable: true,
			};
		}

		if (!servicePrice?.price)
			return {
				key,
				title: value,
				price: undefined,
				isLocalAverage: false,
				isStartingPrice: false,
				isFree: false,
				isUnavailable: false,
			};

		const priceWithGuestSuffix =
			isPerGuest && servicePrice.price !== 'Contact for price'
				? `${servicePrice.price} / guest`
				: servicePrice.price;
		const isLocalAverage =
			!servicePrice.isFree && servicePrice.sourceType !== 'VENDOR';
		const isStartingPrice =
			!servicePrice.isFree &&
			!isLocalAverage &&
			priceWithGuestSuffix !== 'Contact for price';

		return {
			key,
			title: value,
			price: priceWithGuestSuffix,
			isLocalAverage,
			isStartingPrice,
			isFree: servicePrice.isFree === true,
			isUnavailable: false,
		};
	});

	return (
		<div className={Styles.servicesList}>
			<hr className={Styles.divider} />
			<TextBody size="lg" bold className={Styles.servicesTitle}>
				Services
			</TextBody>
			{showMinimumSpendInfo && <ServicesMinimumSpendInfo />}
			{items.map((item, index) => (
				<React.Fragment key={item.key}>
					{index > 0 && <hr className={Styles.divider} />}
					<div className={Styles.serviceRow}>
						<Checkbox
							checked={selectedKeys.includes(item.key)}
							onChange={() => onToggle(item.key)}
							disabled={item.isUnavailable}
						>
							{item.title}
						</Checkbox>
						<div className={Styles.servicePrice}>
							{item.isUnavailable ? (
								<TextBody size="md">{NOT_OFFERED_LABEL}</TextBody>
							) : (
								item.price && (
									<>
										{item.isFree ? (
											<TextBody size="md">{FREE_SERVICE_LABEL}</TextBody>
										) : (
											<TextBody size="md" bold>
												{item.price}
											</TextBody>
										)}
										{item.isLocalAverage && (
											<LocalAverageTooltip variant="editEstimateForm" />
										)}
										{item.isStartingPrice && (
											<TextBody size="xs" asTyped color="subtle-dark">
												Starting price
											</TextBody>
										)}
									</>
								)
							)}
						</div>
					</div>
				</React.Fragment>
			))}
		</div>
	);
};
